import { CodeOutlined, DeleteOutlined, FormOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Card, Input, Modal, Select, Switch, Table } from 'antd'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ItemResponseTrackerBeanObject, ListPagination, TrackerBean } from 'src/api/api'
import { StorageKey, memoryStorage, storageReaderFactory, storageWriterFactory } from 'src/util/Storage'
import { useDebounce } from 'src/util/useDebounce'
import { toSort, useSorter } from 'src/util/useSorter'
import trackerApi from './TrackerApi'
import { env } from '../../../env'

const Column = Table.Column
const Option = Select.Option
const { TextArea } = Input

const buildTag = (tracker: TrackerBean): string =>
    `<!-- Ten DSP Tracker: ${tracker.name} -->
<script>
(function () {
    var TRACKER_URL = '${env.tracker}';
    var ID = ${tracker.id};
    var SESSION_TTL = 30 * 60 * 1000; // new session after 30 min of inactivity
    var KEY = 'ten_tracker_session_' + ID;
    function isNewSession() {
        try {
            var now = Date.now();
            var last = parseInt(localStorage.getItem(KEY), 10);
            localStorage.setItem(KEY, now);
            return !last || (now - last) > SESSION_TTL;
        } catch (e) {
            return false;
        }
    }
    var url = TRACKER_URL + '?id=' + ID + (isNewSession() ? '&session=true' : '');
    new Image().src = url;
})();
</script>`

const storageWriter = storageWriterFactory('tracker', memoryStorage)
const storageReader = storageReaderFactory('tracker', memoryStorage)

interface Props { }

const TrackerList: React.FC<Props> = () => {

    const [data, setData] = useState<TrackerBean[]>([])
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })

    const [search, setSearch] = useState<string>()
    const debouncedSearch = useDebounce(search, 500)

    const savedParams = storageReader.read(StorageKey.SearchParams)
    const isInitialLoad = useRef(true)

    const [active, setActive] = useState<boolean | undefined>(savedParams.active ?? undefined)

    const [tagTracker, setTagTracker] = useState<TrackerBean | undefined>(undefined)

    const remove = (record: TrackerBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: async () => {
                await trackerApi.delete(record.id as number)
                let page = pagination.current
                if (data.length == 1 && pagination.current > 1) {
                    page -= 1
                }
                loadData(page)
            }
        })
    }

    const activate = async (record: TrackerBean, index) => {
        await trackerApi.activate(record.id)

        const bean = data[index]
        bean.active = !record.active

        setData([...data])
    }

    const loadData = async (page: number) => {

        const response = await trackerApi.list(pagination.defaultPageSize, page, toSort(sorter), search, active)

        const array = response.data as Array<ItemResponseTrackerBeanObject>
        const trackerBeans: TrackerBean[] = array.map(item => item.data as TrackerBean)

        if (trackerBeans.length == 0 && page > 1) {
            loadData(page - 1)
        } else {
            const newPagination: ListPagination = response.pagination as ListPagination
            storageWriter.write(StorageKey.SearchParams, {
                page: newPagination.currentPage,
                active
            })

            setData(trackerBeans)
            setPagination({ ...pagination, total: newPagination.total, current: newPagination.currentPage })
        }
    }

    const handleActiveChange = (value: boolean | undefined) => {
        setActive(value)
        storageWriter.write(StorageKey.SearchParams, { active: value ?? null })
    }

    const { sorter, handleTableChange } = useSorter(pagination, loadData)

    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false
            loadData(savedParams.page || pagination.current)
        } else {
            loadData(1)
        }
    }, [debouncedSearch, active, sorter])

    return <Card
        title="Tracker"
        className="list-card"
        extra={<Link to={`/tracker/create`} className="create-new-link">
            <Button type="primary" htmlType="button" icon={<PlusOutlined />} >
                New Tracker
            </Button>
        </Link>}
    >
        <div className="list-filter-group">
            <Input prefix={<SearchOutlined />} placeholder="Search by ID, Name" onChange={e => setSearch(e.target.value || undefined)} />
            <Select placeholder="Filter by Active" value={active} onChange={handleActiveChange} allowClear>
                {Object.values(['Active', 'Inactive']).map(type => <Option key={type} value={type == 'Active'}>{type}</Option>)}
            </Select>
        </div>
        <Table
            rowKey="id"
            size="middle"
            dataSource={data}
            pagination={{ ...pagination, size: 'default' }}
            onChange={handleTableChange}
        >
            <Column key="id" title="ID" dataIndex="id" sorter />
            <Column key="name" title="Name" dataIndex="name" sorter render={(text, record: TrackerBean) => <Link to={`/tracker/${record.id}/edit`}>{text}</Link>} />
            <Column key="domain" title="Domain" dataIndex="domain" />
            <Column
                align="center"
                key="active"
                title="Active"
                dataIndex="active"
                render={(text, record: TrackerBean, index) => <Switch size="small" checked={record.active} onChange={e => activate(record, index)} />}
            />
            <Column
                key="modified"
                title="Last Modified"
                dataIndex="modified"
                sorter
            />
            <Column align="center" key="action" title="Actions" render={(text: string, record: TrackerBean) =>
                <span>
                    <Button type="text" onClick={() => setTagTracker(record)}>
                        <CodeOutlined className="actions-btn" />
                    </Button>
                    <Link to={`/tracker/${record.id}/edit`}>
                        <Button type="text" >
                            <FormOutlined className="actions-btn" />
                        </Button>
                    </Link>
                    <Button type="text" danger onClick={() => remove(record)}>
                        <DeleteOutlined />
                    </Button>
                </span>
            } />
        </Table>
        <Modal
            title={tagTracker ? `JS-tag: ${tagTracker.name}` : 'JS-tag'}
            open={!!tagTracker}
            onCancel={() => setTagTracker(undefined)}
            footer={<Button onClick={() => setTagTracker(undefined)}>Close</Button>}
        >
            <TextArea
                readOnly
                value={tagTracker ? buildTag(tagTracker) : ''}
                autoSize
                style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                onFocus={e => e.target.select()}
            />
        </Modal>
    </Card>
}

export default TrackerList



// WEBPACK FOOTER //
// ./src/component/app/tracker/TrackerList.tsx